// [S-410] Studio Agents chat — TanStack Query hooks + the NDJSON message
// stream, all over the admin proxy (`/chat/*`). Sessions are per-user and
// re-openable; the message stream emits ChatAgentEvent lines (text_delta,
// tool_call, tool_result, usage, done/saved/error).
import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ActiveRecord, RecordRef } from "@csai/core";
import { ACTIVE_TENANT_HEADER, useApi, useApiStream } from "./api";
import { RETRY_IF_UNREACHABLE } from "./queryClient";

export type { ActiveRecord, RecordRef };

export type ChatAgentKind =
  | "assistant"
  | "drafting"
  | "analysis"
  | "negotiation"
  | "longform";

export type ChatBlock =
  | { type: "text"; text: string }
  | { type: "tool_use"; id: string; name: string; input: unknown }
  | { type: "tool_result"; toolUseId: string; result: unknown; isError?: boolean };

export type ChatSessionDTO = {
  id: string;
  tenantId: string;
  uid: string;
  agentKind: ChatAgentKind;
  // [S-666a] Set by an in-app routing handoff (route_to_agent); null means
  // "same as agentKind" (the common case — a session that hasn't routed).
  // Absent on an older cached response — treat as null.
  activeAgentKind?: ChatAgentKind | null;
  // The draft this chat was opened from (Draft Workspace), fixed at creation.
  activeDraftId?: string | null;
  title: string;
  messageCount: number;
  attachments: Array<{ id: string; filename: string }>;
  createdAt: string;
  updatedAt: string;
};

// [S-716] The wire payload for GET /chat/sessions/:id already carries the
// full ChatSession — including each attachment's extractedText — since the
// admin proxy forwards it unstripped (chat.ts). ChatSessionDTO's own
// `attachments` stays the lighter {id, filename} shape for the list view
// (useChatSessions); this widens just the detail hook's response, where the
// citation attachment viewer needs the text.
export type ChatAttachmentDetail = {
  id: string;
  filename: string;
  extractedText: string;
};

export type ChatMessageDTO = {
  id: string;
  seq: number;
  role: "user" | "assistant";
  content: ChatBlock[];
  // [S-412] Openable records the assistant cited this turn (precedents /
  // templates). Rendered as clickable cards; defaulted for older messages.
  references?: RecordRef[];
  // [S-650] Files the user attached WITH this turn — rendered inline on the
  // message (claude.ai style), not as a persistent session pill.
  attachments?: Array<{ id: string; filename: string }>;
  // [context-picker] Records attached from the composer's template / draft
  // pickers, carried back so the chip survives a reload like a file does.
  picked?: Array<{ kind: "draft" | "template"; id: string; title: string }>;
  createdAt: string;
};

// [S-507] One field of an in-chat confirmation form. The agent emits an
// `elicit` event mid-stream with a small set of these so the user can confirm
// or edit auto-detected parameters (e.g. dealType / guild) before a tool runs.
export type ElicitField = {
  name: string;
  label: string;
  /** [S-769] "draft" is a RECORD PICK — the app opens its own draft picker and
   *  answers with the draft's id. `options` is still sent beside it, so an
   *  older client falls back to the select. Mirrors ElicitField in
   *  packages/core/src/ports/ChatAgentPort.ts. */
  type: "select" | "text" | "draft";
  options?: string[];
  /** [S-769] What the picker opens narrowed to, when the asking tool narrowed. */
  documentForm?: "deal_memo" | "long_form";
  suggested?: string;
  required?: boolean;
};

// [S-509b] Clause-by-clause review walkthrough. The agent streams a `review_step`
// event each time the plan advances (scope, a decision recorded, prev/next nav,
// or completion). The web keeps the LATEST payload and drives a right-hand review
// pane from it — each event supersedes the previous one.
export type ReviewStepDecision = "pending" | "applied" | "kept" | "skipped";

// [S-509h/M1] Deterministic triage risk — the plan leads with what matters.
export type ReviewStepRisk = "high" | "medium" | "low";

// [completeness-first] Which reconciliation bucket a draft clause is in —
// "matched" (has a certified counterpart) or "additional" (extra content the
// standard doesn't cover; never a deviation) or "missing" (a standard clause the
// draft lacks; Approve adds it — review-add 2026-10-21).
export type ReviewStepCategory = "matched" | "additional" | "missing";

/** One clause in the walkthrough (drives the segmented progress bar + log). */
export type ReviewStepItem = {
  index: number; // 0-based position in the plan
  name: string;
  // The clause's number as written in the DOCUMENT ("6" for "6. Credit"); null
  // when it has none (hide it). Optional: older payloads omit it.
  displayNumber?: string | null;
  category: ReviewStepCategory;
  decision: ReviewStepDecision;
  risk: ReviewStepRisk;
  // [P3] True when this (additional) clause contradicts a standard clause.
  hasConflict: boolean;
  // [walkthrough-actionable] True when this clause needs a decision (has a
  // recommendation or a conflict). Compliant / clean-additional clauses are false.
  needsReview: boolean;
  // Short name + one-line why, so a decided clause's card still reads after a reload.
  shortName?: string;
  whyLine?: string | null;
};

/** [P3] A contradiction between an additional clause and a standard clause. */
export type ReviewStepConflict = { standardClauseName: string; reason: string };

/** The clause currently under review (the review-pane body). */
export type ReviewStepCurrent = {
  index: number; // 0-based
  // SHORT clause name ("Credit"), not truncated clause text.
  name: string;
  // The clause's number as written in the DOCUMENT; null when it has none.
  displayNumber?: string | null;
  category: ReviewStepCategory;
  draftText: string;
  risk: ReviewStepRisk;
  // [P3] Contradictions with the standard (additional clauses only; else empty).
  conflicts: ReviewStepConflict[];
  // [2026-10-21] A figure far outside comparable deals (exclusivity weeks):
  // nothing to apply, the user keeps or skips it.
  outlier?: { term: string; summary: string } | null;
  // [review-undo-honest] Why the last Undo could not revert the draft — the
  // clause stays Applied and this says why. Absent otherwise.
  decisionNote?: string;
  // Substring of `draftText` that deviates from the certified standard; rendered
  // struck through. Null when the clause matches the standard.
  deviationSpan: string | null;
  recommendation: {
    text: string;
    sourceLabel: string;
    why: string;
    // One plain-language sentence built from the same term diff as `why`. Null
    // when it can't be phrased; fall back to `why`. Optional: older payloads omit it.
    whyPlain?: string | null;
    // Index of the matched clause inside the template `templateId` names — pass
    // it as `clause` to useReviewSourceExcerpt("template", ...). Null when unknown.
    standardClauseIndex?: number | null;
    // [Fix 5] The certified template this recommendation cites — the source chip
    // opens it (view-only) in the rail. Null when no template backs it.
    templateId: string | null;
    // [S-509g] L2 precedent facts behind this recommendation — a deterministic
    // summary ("3 comparable past deals found") plus a few comparables and their
    // term snippets. Kept STRICTLY separate from the certified (L1) standard
    // above. Null when no comparable precedent was found.
    precedent: {
      summary: string;
      comparables: Array<{
        title: string;
        term: string;
        precedentId?: string | null;
        // The matched clause inside that precedent — pass it as `clause` to
        // useReviewSourceExcerpt("precedent", ...).
        clauseId?: string | null;
      }>;
    } | null;
    // [S-509g] An optional one-line strategic note the agent draws from the
    // precedent — its read of what the past deals imply. Null when there's none.
    take: string | null;
    // [P4] A coherence warning when applying this certified text would clash with
    // the draft's own wording (mismatched defined terms, a dropped specific).
    coherenceNote: string | null;
    // [review-remove] "remove" = Approve takes this ADDED clause out of the draft
    // (it contradicts the standard; `text` is empty). Absent = replace with `text`.
    // "add" = Approve inserts this standard clause the draft is missing.
    action?: "replace" | "remove" | "add";
  } | null;
};

export type ReviewStepEvent = {
  type: "review_step";
  sessionId: string;
  total: number;
  currentIndex: number; // 0-based; === total when complete
  counts: { applied: number; kept: number; skipped: number; left: number };
  // [async-review] "building" — the plan is still computing in the background (the
  // chat tool returned immediately so the turn never held a long request open);
  // the pane shows a "reviewing…" placeholder and the session query polls until it
  // flips. "failed" — the background build errored (see `error`); the pane stops
  // polling and offers retry.
  status: "building" | "in_progress" | "complete" | "failed";
  // [async-review] Present when status === "failed": a short, user-facing reason.
  error?: string | null;
  // [S-509f] True at the "decide on your skips" prompt point: every clause is
  // applied/kept/skipped, ≥1 is skipped, and the user hasn't chosen yet whether
  // to walk the skips or finish. When true the pane shows that prompt instead of
  // a clause body and `current` may be null (rather than auto-looping back to a
  // skipped clause). Resolved via the resolve-skips endpoint below.
  awaitingSkipReview: boolean;
  steps: ReviewStepItem[];
  current: ReviewStepCurrent | null;
  // [after-review-inline] The Workspace draft under review (null for an upload),
  // so the inline UI can open it in the document rail beside the clause cards.
  sourceDraftId?: string | null;
  // [completeness-first] Certified clauses the standard expects but the draft is
  // MISSING — surfaced so the review can prompt "consider adding" (informational).
  missingClauses?: Array<{ name: string; text: string; sourceLabel: string }>;
  // [walkthrough-actionable] Clauses needing no action (compliant / clean-
  // additional) — summarized rather than carded. total/counts cover only the
  // clauses that need review.
  noActionCount?: number;
  // Present once the walkthrough completes (S-509c materializes the draft).
  finalized?: { draftId: string | null; applied: number; kept: number };
  // [S-509h/M0b] Set when the last action re-read a draft edited mid-review and
  // re-analyzed it. The plan moved under the click, so the pane shows the
  // refreshed clauses and the chat narrates "the draft changed — I re-read it"
  // instead of the (not-applied) decision. Absent on an ordinary action.
  reconciled?: { modified: number; added: number; deleted: number } | null;
};

// [S-509d] The active review restored on session-fetch — the same payload as a
// `review_step` event, minus the `type` discriminant. The backend persists the
// walkthrough so reopening a chat mid-review can rehydrate the pane; null when no
// review is in progress for that session. The web seeds the pane from this, and a
// subsequent live `review_step` supersedes it as usual.
export type ReviewStep = Omit<ReviewStepEvent, "type">;

export type ChatStreamEvent =
  | { type: "text_delta"; text: string }
  | { type: "tool_call"; tool: string; args: unknown }
  | { type: "tool_result"; tool: string; ok: boolean; references?: RecordRef[] }
  // [S-507] Inline confirmation form: prompt + fields with suggested values.
  | { type: "elicit"; tool: string; prompt: string; fields: ElicitField[] }
  // [S-509b] Clause-by-clause review walkthrough step.
  | ReviewStepEvent
  // [S-516] generate_draft produced an editable Workspace draft — render an
  // "Open in Draft Workspace" card, not a raw URL.
  // [S-669] action distinguishes a new draft from an in-place update.
  | {
      type: "draft_generated";
      draftId: string;
      title: string;
      url: string;
      action: "created" | "updated";
      /** [S-798] Present while the content is still generating after the reply. */
      status?: "building";
      /** [S-802] Which generation this event announces (its build startedAt). */
      startedAt?: string;
    }
  | { type: "usage"; inputTokens: number; outputTokens: number; totalTokens: number }
  | { type: "done"; stopReason: string }
  // [S-802] `text` = the reply as persisted, after the server's policy rewrites.
  | { type: "saved"; stopReason: string; references?: RecordRef[]; text?: string }
  | { type: "error"; message: string };

export const CHAT_KEY = ["chat", "sessions"] as const;

function scopeSuffix(tenantId?: string): string {
  return tenantId !== undefined && tenantId.length > 0
    ? `?tenantId=${encodeURIComponent(tenantId)}`
    : "";
}

// Pin the active-tenant header to the tenant a chat request is scoped to, so it
// always reaches the server even if the derived session.activeTenantId hasn't
// settled yet (the chat list/sidebar can fire before then). applyTenantAndDevShim
// won't overwrite a header we set here.
function tenantHeader(tenantId?: string): { headers: Record<string, string> } | undefined {
  return tenantId !== undefined && tenantId.length > 0
    ? { headers: { [ACTIVE_TENANT_HEADER]: tenantId } }
    : undefined;
}

/** A user's sessions (newest-updated first), optionally filtered to one agent. */
export function useChatSessions(opts: {
  agentKind?: ChatAgentKind;
  tenantId?: string;
  enabled?: boolean;
}) {
  const api = useApi("admin");
  return useQuery({
    queryKey: [...CHAT_KEY, opts.tenantId ?? "self", opts.agentKind ?? "all"],
    enabled: opts.enabled ?? true,
    queryFn: async () => {
      const out = await api<{ sessions: ChatSessionDTO[] }>(
        `/chat/sessions${scopeSuffix(opts.tenantId)}`,
        tenantHeader(opts.tenantId)
      );
      return opts.agentKind === undefined
        ? out.sessions
        : out.sessions.filter((s) => s.agentKind === opts.agentKind);
    },
  });
}

/** One session + its messages (for re-opening from history). `enabled` lets a
 *  caller skip the fetch entirely — e.g. a just-created session that already has
 *  its data in hand and no messages to load yet (avoids a needless GET that can
 *  race the create write). */
export function useChatSessionDetail(
  sessionId: string | null,
  tenantId?: string,
  enabled = true
) {
  const api = useApi("admin");
  return useQuery({
    queryKey: [...CHAT_KEY, "detail", sessionId, tenantId ?? "self"],
    enabled: enabled && sessionId !== null,
    queryFn: async () => {
      const out = await api<{
        session: Omit<ChatSessionDTO, "attachments"> & {
          attachments: ChatAttachmentDetail[];
        };
        messages: ChatMessageDTO[];
        // [S-509d] An in-progress clause review to restore the pane from; absent
        // on older backends, null when no review is active for this session.
        activeReview?: ReviewStep | null;
      }>(
        `/chat/sessions/${encodeURIComponent(sessionId!)}${scopeSuffix(tenantId)}`,
        tenantHeader(tenantId)
      );
      return {
        session: out.session,
        messages: out.messages,
        activeReview: out.activeReview ?? null,
      };
    },
    // [async-review] While the walkthrough plan is still building in the
    // background, poll so the pane flips from "reviewing…" to the first clause the
    // moment the build lands. Stop as soon as it leaves `building` (in_progress /
    // complete / failed are terminal for the build).
    refetchInterval: (query) =>
      query.state.data?.activeReview?.status === "building" ? 2000 : false,
  });
}

export function useCreateChatSession() {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      agentKind: ChatAgentKind;
      title?: string;
      tenantId?: string;
      // [S-521] Set when this chat is opened FROM a specific draft's Chat
      // panel (DraftDetail) — persisted server-side as activeDraftId so the
      // drafting persona knows which draft is open without the user
      // re-naming it.
      draftId?: string;
    }) =>
      api<ChatSessionDTO>("/chat/sessions", {
        method: "POST",
        ...tenantHeader(args.tenantId),
        body: {
          agentKind: args.agentKind,
          ...(args.title !== undefined ? { title: args.title } : {}),
          ...(args.tenantId !== undefined ? { tenantId: args.tenantId } : {}),
          ...(args.draftId !== undefined ? { draftId: args.draftId } : {}),
        },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CHAT_KEY });
    },
  });
}

/** Returns a fn that streams one assistant turn, invoking onEvent per line.
 *  [rail epic] `openRecord`: undefined = this caller has no rail (leave the
 *  session's activeRecord alone); null/an ActiveRecord = the rail's current
 *  state, sent every turn so the session always reflects what's open now. */
export function useChatMessageStream(): (
  sessionId: string,
  text: string,
  tenantId: string | undefined,
  onEvent: (event: ChatStreamEvent) => void,
  attachmentIds?: string[],
  openRecord?: ActiveRecord | null,
  // [chat-stop] Aborts the streaming turn when the user hits Stop.
  signal?: AbortSignal,
  // [context-picker] Records the user picked from the composer for THIS turn.
  picks?: { draftIds?: string[]; templateId?: string }
) => Promise<void> {
  const stream = useApiStream("admin");
  return useCallback(
    async (sessionId, text, tenantId, onEvent, attachmentIds, openRecord, signal, picks) => {
      await stream(
        `/chat/sessions/${encodeURIComponent(sessionId)}/messages`,
        {
          method: "POST",
          headers: {
            "content-type": "application/json",
            ...(tenantId !== undefined && tenantId.length > 0
              ? { [ACTIVE_TENANT_HEADER]: tenantId }
              : {}),
          },
          body: JSON.stringify({
            text,
            ...(attachmentIds !== undefined && attachmentIds.length > 0
              ? { attachmentIds }
              : {}),
            ...(openRecord !== undefined ? { openRecord } : {}),
            ...((picks?.draftIds?.length ?? 0) > 0
              ? { pickedDraftIds: picks?.draftIds }
              : {}),
            ...((picks?.templateId ?? "").length > 0
              ? { pickedTemplateId: picks?.templateId }
              : {}),
            ...(tenantId !== undefined ? { tenantId } : {}),
          }),
          ...(signal !== undefined ? { signal } : {}),
        },
        (line) => {
          try {
            onEvent(JSON.parse(line) as ChatStreamEvent);
          } catch {
            // ignore non-JSON keepalive lines
          }
        }
      );
    },
    [stream]
  );
}

/** [chat-regenerate] Re-answer the last user turn after a Stop. Streams exactly
 *  like useChatMessageStream (same event handling), but hits /retry with no text
 *  — the backend re-runs the user turn already in history (dropping the trailing
 *  stop marker). Used by the composer's Regenerate control on a stopped turn. */
export function useChatRetry(): (
  sessionId: string,
  tenantId: string | undefined,
  onEvent: (event: ChatStreamEvent) => void,
  signal?: AbortSignal,
  text?: string
) => Promise<void> {
  const stream = useApiStream("admin");
  return useCallback(
    async (sessionId, tenantId, onEvent, signal, text) => {
      await stream(
        `/chat/sessions/${encodeURIComponent(sessionId)}/retry`,
        {
          method: "POST",
          headers: {
            "content-type": "application/json",
            ...(tenantId !== undefined && tenantId.length > 0
              ? { [ACTIVE_TENANT_HEADER]: tenantId }
              : {}),
          },
          // [retry-unsent] The retried message's text: if it never reached the
          // server (a failed send), the server sends it instead of re-answering
          // the turn before it.
          body: JSON.stringify({
            ...(tenantId !== undefined ? { tenantId } : {}),
            ...(text !== undefined && text.length > 0 ? { text } : {}),
          }),
          ...(signal !== undefined ? { signal } : {}),
        },
        (line) => {
          try {
            onEvent(JSON.parse(line) as ChatStreamEvent);
          } catch {
            // ignore non-JSON keepalive lines
          }
        }
      );
    },
    [stream]
  );
}

/** [chat-stop] Explicitly cancel the in-flight turn for a session. The composer's
 *  Stop calls this IN ADDITION to aborting the local stream — App Hosting holds the
 *  streaming connection open until the turn completes, so the socket abort never
 *  reaches the backend; this dedicated request is what actually stops the server
 *  turn (the agent loop bails and a "[stopped]" marker is recorded). Best-effort. */
export function useChatCancel() {
  const api = useApi("admin");
  return useMutation({
    mutationFn: async (args: { chatSessionId: string; tenantId?: string }) =>
      api<{ cancelled: boolean }>(
        `/chat/sessions/${encodeURIComponent(args.chatSessionId)}/cancel`,
        {
          method: "POST",
          ...tenantHeader(args.tenantId),
          body: args.tenantId !== undefined ? { tenantId: args.tenantId } : {},
        }
      ),
  });
}

/** Permanently delete a chat session (and its messages) from the system. */
export function useDeleteChatSession() {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { sessionId: string; tenantId?: string }) =>
      api<{ ok: boolean }>(
        `/chat/sessions/${encodeURIComponent(args.sessionId)}${scopeSuffix(args.tenantId)}`,
        { method: "DELETE", ...tenantHeader(args.tenantId) }
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CHAT_KEY });
    },
  });
}

// [S-666e] Manual override — "switch back" from a routed persona to this
// session's pinned agentKind. A plain mutation, not tied to the LLM loop.
export function useResetActiveAgent() {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { sessionId: string; tenantId?: string }) =>
      api<ChatSessionDTO>(
        `/chat/sessions/${encodeURIComponent(args.sessionId)}/reset-agent`,
        { method: "POST", ...tenantHeader(args.tenantId) }
      ),
    onSuccess: (_data, args) => {
      void qc.invalidateQueries({
        queryKey: [...CHAT_KEY, "detail", args.sessionId],
      });
    },
  });
}

// [S-509e] Deterministic review-pane actions. The clause-by-clause walkthrough
// no longer round-trips its controls through the chat/LLM — each button hits a
// dedicated backend endpoint that records the decision (or navigates) and returns
// the next `ReviewStep` payload. The chat session id rides in the path (like the
// other /chat mutations); the reviewSessionId rides in the body. Tenant scope is
// the same active-tenant header the other chat calls pin.

/** Record a clause decision (apply / keep / skip) at a given index, or "undo" to
 *  reverse a prior apply (reverts the draft edit and returns the clause to pending). */
export function useReviewDecide() {
  const api = useApi("admin");
  return useMutation({
    mutationFn: async (args: {
      chatSessionId: string;
      reviewSessionId: string;
      index: number;
      decision: "apply" | "keep" | "skip" | "undo";
      tenantId?: string;
    }) =>
      api<{ reviewStep: ReviewStep }>(
        `/chat/sessions/${encodeURIComponent(args.chatSessionId)}/review/decide`,
        {
          method: "POST",
          ...tenantHeader(args.tenantId),
          body: {
            reviewSessionId: args.reviewSessionId,
            index: args.index,
            decision: args.decision,
          },
        }
      ),
  });
}

/** [Fix 3] Rewrite one clause's recommendation per a reviewer instruction (the
 *  card's inline "Type" box). Returns the refreshed step so the card updates. */
export function useReviewRevise() {
  const api = useApi("admin");
  return useMutation({
    mutationFn: async (args: {
      chatSessionId: string;
      reviewSessionId: string;
      index: number;
      instruction: string;
      tenantId?: string;
    }) =>
      api<{ reviewStep: ReviewStep }>(
        `/chat/sessions/${encodeURIComponent(args.chatSessionId)}/review/revise`,
        {
          method: "POST",
          ...tenantHeader(args.tenantId),
          body: {
            reviewSessionId: args.reviewSessionId,
            index: args.index,
            instruction: args.instruction,
          },
        }
      ),
  });
}

/** Navigate the walkthrough to a clause index (no decision recorded). */
export function useReviewGoto() {
  const api = useApi("admin");
  return useMutation({
    mutationFn: async (args: {
      chatSessionId: string;
      reviewSessionId: string;
      index: number;
      tenantId?: string;
    }) =>
      api<{ reviewStep: ReviewStep }>(
        `/chat/sessions/${encodeURIComponent(args.chatSessionId)}/review/goto`,
        {
          method: "POST",
          ...tenantHeader(args.tenantId),
          body: { reviewSessionId: args.reviewSessionId, index: args.index },
        }
      ),
  });
}

/** Resolve the leftover skipped clauses once the walkthrough reaches the end.
 *  `choice:"review"` navigates to the first skipped clause so the user walks the
 *  skips; `choice:"finish"` completes the review, leaving the skips as-is (the
 *  ghost "Create draft" card then appears). Mirrors useReviewDecide/useReviewGoto:
 *  chat session id in the path, reviewSessionId in the body, tenant via header. */
export function useReviewResolveSkips() {
  const api = useApi("admin");
  return useMutation({
    mutationFn: async (args: {
      chatSessionId: string;
      reviewSessionId: string;
      choice: "review" | "finish";
      tenantId?: string;
    }) =>
      api<{ reviewStep: ReviewStep }>(
        `/chat/sessions/${encodeURIComponent(args.chatSessionId)}/review/resolve-skips`,
        {
          method: "POST",
          ...tenantHeader(args.tenantId),
          body: { reviewSessionId: args.reviewSessionId, choice: args.choice },
        }
      ),
  });
}

/** Materialize the reviewed draft into the Workspace (explicit, opt-in step). */
export function useReviewCreateDraft() {
  const api = useApi("admin");
  return useMutation({
    mutationFn: async (args: {
      chatSessionId: string;
      reviewSessionId: string;
      tenantId?: string;
    }) =>
      api<{ reviewStep: ReviewStep }>(
        `/chat/sessions/${encodeURIComponent(args.chatSessionId)}/review/create-draft`,
        {
          method: "POST",
          ...tenantHeader(args.tenantId),
          body: { reviewSessionId: args.reviewSessionId },
        }
      ),
  });
}

export function useUploadChatAttachment() {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      sessionId: string;
      file: File;
      tenantId?: string;
    }) => {
      const form = new FormData();
      form.append("file", args.file, args.file.name);
      if (args.tenantId !== undefined) form.append("tenantId", args.tenantId);
      // [bugfix] `attachmentWarning` is a non-fatal heads-up (today: a PDF
      // whose extracted-text-per-page density is suspiciously low, e.g. a
      // mixed scanned/text document) merged onto the normal response by the
      // admin proxy — additive, absent on every ordinary upload.
      return api<ChatSessionDTO & { attachmentWarning?: string }>(
        `/chat/sessions/${encodeURIComponent(args.sessionId)}/attachments`,
        { method: "POST", ...tenantHeader(args.tenantId), body: form }
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CHAT_KEY });
    },
    // [dev-loop bugfix] Ride out a backend that isn't answering (a dev-loop
    // restart after a packages/* rebuild; a deploy rollover or cold start in
    // prod) instead of failing the attach permanently on the first refused
    // connection. Only unreachable errors retry — an unsupported extension or
    // an unreadable scan still fails immediately with the server's own message.
    // Safe here: the request carries the file, so a repeat either lands once or
    // stages a duplicate attachment the user can see and remove.
    ...RETRY_IF_UNREACHABLE,
  });
}

/** Collapse a stored message's blocks to display text. */
export function messageText(m: ChatMessageDTO): string {
  return m.content
    .filter((b): b is Extract<ChatBlock, { type: "text" }> => b.type === "text")
    .map((b) => b.text)
    .join("");
}

/** [S-516] Recover a generated-draft card from a persisted message's tool_result
 *  blocks, so the "Open in Draft Workspace" card survives reload (the live
 *  `draft_generated` event is only sent once, during the turn). `generate_draft`
 *  stamps a top-level `draftGenerated` on its result; we read it back here.
 *  Returns the LAST one in the message (one generate_draft per turn in practice).
 *  [S-669] `action` defaults to "created" for messages persisted before this
 *  field existed — same forward-compat default as the adapter's extraction. */
export type DraftGeneratedMarker = { draftId: string; title: string; url: string; action: "created" | "updated"; status?: "building"; startedAt?: string };

/** [elicit-reload] The form an assistant turn asked, from its persisted tool
 *  result (the live `elicit` event fires once). Live 2026-09-18: reopening a
 *  chat whose last reply was a form showed nothing to answer. */
export function elicitFromMessage(m: ChatMessageDTO): { tool: string; prompt: string; fields: ElicitField[] } | undefined {
  const uses = new Map(m.content.flatMap((b) => (b.type === "tool_use" ? [[b.id, b.name] as const] : [])));
  for (const b of m.content) {
    if (b.type !== "tool_result") continue;
    const e = (b.result as { elicit?: { prompt?: unknown; fields?: unknown } } | null)?.elicit;
    if (e !== undefined && typeof e.prompt === "string" && Array.isArray(e.fields))
      return { tool: uses.get(b.toolUseId) ?? "request_parameters", prompt: e.prompt, fields: e.fields as ElicitField[] };
  }
  return undefined;
}

export function draftGeneratedFromMessage(m: ChatMessageDTO): DraftGeneratedMarker | undefined {
  let found: DraftGeneratedMarker | undefined;
  for (const b of m.content) {
    if (b.type !== "tool_result") continue;
    const dg = (b.result as { draftGenerated?: unknown } | null)?.draftGenerated;
    if (dg === null || typeof dg !== "object") continue;
    const { draftId, title, url, action, status, startedAt } = dg as {
      draftId?: unknown;
      title?: unknown;
      url?: unknown;
      action?: unknown;
      status?: unknown;
      startedAt?: unknown;
    };
    if (typeof draftId === "string" && typeof url === "string") {
      found = {
        draftId,
        title: typeof title === "string" ? title : "Draft",
        url,
        action: action === "updated" ? "updated" : "created",
        // [S-798] The card keeps polling the draft until this build lands.
        ...(status === "building" ? { status } : {}),
        // [S-802] …and only until ITS build lands: without this a retry's
        // build was read by the reloaded earlier cards too.
        ...(typeof startedAt === "string" ? { startedAt } : {}),
      };
    }
  }
  return found;
}
